import React, { useEffect } from "react";
import { Modal, Form, Input, InputNumber, Checkbox } from "antd";
import { TaskTemplateItem } from "../../../../../types/taskTemplate";

interface TaskTemplateItemModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (item: TaskTemplateItem) => void;
  item?: TaskTemplateItem | null;
  mode: "create" | "edit";
}

const TaskTemplateItemModal: React.FC<TaskTemplateItemModalProps> = ({
  open,
  onClose,
  onSave,
  item,
  mode,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      if (mode === "edit" && item) {
        form.setFieldsValue({
          name: item.name,
          description: item.description,
          category: item.category,
          estimatedMinutes: item.estimatedMinutes,
          requirePhoto: item.requirePhoto,
          requireNote: item.requireNote,
          sortOrder: item.sortOrder,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          requirePhoto: false,
          requireNote: false,
          estimatedMinutes: 0,
          sortOrder: 0,
        });
      }
    }
  }, [open, mode, item, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const itemData: TaskTemplateItem = {
        id: item?.id,
        name: values.name,
        description: values.description,
        category: values.category,
        estimatedMinutes: values.estimatedMinutes || 0,
        requirePhoto: values.requirePhoto || false,
        requireNote: values.requireNote || false,
        sortOrder: values.sortOrder || 0,
      };
      onSave(itemData);
      form.resetFields();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title={mode === "create" ? "Add Task Item" : "Edit Task Item"}
      open={open}
      onOk={handleSubmit}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      okText={mode === "create" ? "Add" : "Save"}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 24 }}
        requiredMark="optional"
      >
        <Form.Item
          name="name"
          label="Item Name"
          rules={[
            { required: true, message: "Please enter item name" },
            { max: 150, message: "Name cannot exceed 150 characters" },
          ]}
        >
          <Input placeholder="e.g., Clean equipment, Check inventory" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 500, message: "Description cannot exceed 500 characters" },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Describe what needs to be done..."
          />
        </Form.Item>

        <Form.Item
          name="category"
          label="Category"
          rules={[
            { max: 50, message: "Category cannot exceed 50 characters" },
          ]}
        >
          <Input placeholder="e.g., Cleaning, Safety Check" />
        </Form.Item>

        <Form.Item
          name="estimatedMinutes"
          label="Estimated Minutes"
          rules={[
            { type: "number", min: 0, message: "Must be a positive number" },
          ]}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder="e.g., 15"
          />
        </Form.Item>

        <Form.Item name="sortOrder" label="Sort Order">
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder="e.g., 1"
          />
        </Form.Item>

        <Form.Item name="requirePhoto" valuePropName="checked">
          <Checkbox>Require Photo</Checkbox>
        </Form.Item>

        <Form.Item name="requireNote" valuePropName="checked">
          <Checkbox>Require Note</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TaskTemplateItemModal;
